import React from "react";
import { defineMessages, FormatDateOptions, useIntl } from "react-intl";

import { Attachment } from "lib/build/attachments";

import {
	ColumnProps,
	DataTable,
	RowData
} from "src/common/components/DataTable";
import { getOriginalFileName } from "src/common/utils/files";
import { useOrgModParams } from "src/features/orgmod";

const messages = defineMessages({
	attachments: {
		id: "attachments",
		defaultMessage: "Leletek, mellékletek"
	},
	timestamp: {
		id: "attachments.timestamp",
		defaultMessage: "Feltöltés ideje"
	},
	filename: {
		id: "attachments.filename",
		defaultMessage: "Fájlnév"
	}
});

const formatDateOptions: FormatDateOptions = {
	year: "numeric",
	month: "numeric",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	second: "numeric"
};

interface AttachmentTableProps {
	attachments: Attachment[];
}

export const AttachmentTable: React.FunctionComponent<AttachmentTableProps> = ({
	attachments
}) => {
	const columns = useColumns();
	return (
		<DataTable
			columns={columns}
			data={attachments as unknown as RowData[]}
		/>
	);
};

function useColumns(): ColumnProps[] {
	const { formatMessage, formatDate } = useIntl();
	const { org, mod } = useOrgModParams();
	return React.useMemo(
		() => [
			{
				key: "timestamp",
				label: formatMessage(messages.timestamp),
				render: createRenderDate(formatDate)
			},
			{
				key: "filename",
				label: formatMessage(messages.filename),
				render: createRenderFilename(org, mod)
			}
		],
		[formatMessage, formatDate, org, mod]
	);
}

function createRenderDate(
	formatDate: (value: string, options: FormatDateOptions) => string
): (value: string) => React.ReactNode {
	return value => formatDate(value, formatDateOptions);
}

function createRenderFilename(
	org: string,
	mod: string
): (value: string) => React.ReactNode {
	return value => (
		<a
			href={`/.netlify/functions/files/${value}?organization=${org}&module=${mod}`}
			rel="noopener noreferrer"
			target="_blank"
			onClick={event => {
				event.stopPropagation();
			}}
		>
			{getOriginalFileName(value)}
		</a>
	);
}
